import gql from "graphql-tag";

import {
  HEALTH_AREA_FIELDS,
  CONSUMER_TEST_PRODUCT_WITH_OPTIONS_CONNECTION_FIELDS,
  TEST_PRODUCT_CONTENT_FIELDS
} from "./shared";
import { TEST_DATA_FILE_FIELDS } from "./tpo/matrix/types";
import {
  CHART_CONFIGURATION_FIELDS,
  USER_RESULT_FIELDS_TRUNCATED,
  USER_TEST_FIELDS_TRUNCATED
} from "./tpo/results/types";

// Queries

export const CLINIC_LOCATION_FIELDS = gql`
  fragment ClinicLocationFields on ClinicLocationType {
    id
    paymentIsoCode
    organisation
    fee
    clinic {
      id
      name
      fee
    }
    address
    postCode
    bookingLink
    phoneNumber
    coords {
      lat
      lng
    }
  }
`;

export const TEST_PRODUCT_OPTION_FAMILY_FIELDS = gql`
  fragment TestProductOptionFamilyFields on TestProductOptionFamilyType {
    id
    name
    description
  }
`;

export const TEST_PRODUCT_OPTION_TRUNCATED_FIELDS = gql`
  fragment TestProductOptionTruncatedFields on TestProductOptionType {
    id
    paymentIsoCode
    organisation
    compositeId
    price
    default
  }
`;

// NB we have two queries for getting clinics
// Because we only want fields not relating to the prices strictly speaking we don't
// need two queries.  After all clinic fee is constant.
// However there is a price field on the ClinicLocationType which does depend on consumer or practitioner shop
// so it's dangerous to use the same query for both.
// We don't use this price field because clinics are gotten within the context of a test product option so
// we already have the price relevant to the shop context already.

export const GET_CONSUMER_CLINICS = gql`
  query GetConsumerClinics($compositeId: ID!, $postCode: String) {
    consumerClinicLocations(compositeId: $compositeId, postCode: $postCode) {
      ...ClinicLocationFields
      distance
    }
  }
  ${CLINIC_LOCATION_FIELDS}
`;

export const GET_PRACTITIONER_CLINICS = gql`
  query GetPractitionerClinics(
    $compositeId: ID!
    $postCode: String
    $paymentIsoCode: String
    $organisation: ID
  ) {
    practitionerClinicLocations(
      compositeId: $compositeId
      postCode: $postCode
      paymentIsoCode: $paymentIsoCode
      organisation: $organisation
    ) {
      ...ClinicLocationFields
      distance
    }
  }
  ${CLINIC_LOCATION_FIELDS}
`;

// TODO - look to remove the clinics list below
// Do we need it?  If it's just for knowing if there are clinics return a boolean instead somewhere

export const CONSUMER_TEST_PRODUCT_OPTION_FIELDS = gql`
  fragment ConsumerTestProductOptionFields on TestProductOptionType {
    id
    paymentIsoCode
    organisation
    compositeId
    testProductOptionFamily {
      ...TestProductOptionFamilyFields
    }
    rrpCurrentPrice
    rrpFullPrice
    consumerClinicsInfo {
      fromPrice
      clinics {
        id
        paymentIsoCode
        organisation
      }
    }
  }
  ${TEST_PRODUCT_OPTION_FAMILY_FIELDS}
`;

export const PRACTITIONER_TEST_PRODUCT_OPTION_FIELDS = gql`
  fragment PractitionerTestProductOptionFields on TestProductOptionType {
    id
    paymentIsoCode
    organisation
    compositeId
    testProductOptionFamily {
      ...TestProductOptionFamilyFields
    }
    tradeCurrentPrice
    tradeFullPrice
    rrpFullPrice
    exvatFullPrice
    practitionerClinicsInfo {
      fromPrice
      clinics {
        id
        paymentIsoCode
        organisation
      }
    }
  }
  ${TEST_PRODUCT_OPTION_FAMILY_FIELDS}
`;

export const TEST_PRODUCT_FIELDS = gql`
  fragment TestProductFields on TestProductType {
    id
    paymentIsoCode
    organisation
    compositeId
    name
    slug
    letters
    subletters
    sector
    productFamily {
      id
      name
      slug
    }
    specifiedSex
    sampleTypes
    healthAreas
    description
    overview
    productCode
    price
    previousPrice
    status
    bundlePromoImage
    bundlePromoImageAltText
    promoImage
    promoImageAltText
    smallPromoImage
    smallPromoImageAltText
    socialImage
    socialImageAltText
    reportPdf
    collectingYourSampleImage
    collectingYourSampleVideo
    shortDescription
    testType
    numOfBiomarkersTested
    processingTime
    productOptionsExplanation
    whyYouShouldTakeThisTest
    whatYouGetWithTest
    shippingAndProcessing
    needHelp
    testDetails
    relatedSymptoms
    biomarkersTested
    metaDescription
    metaTitle
    howItWorks
  }
`;

export const TEST_PRODUCT_SHORT_FIELDS = gql`
  fragment TestProductShortFields on TestProductType {
    id
    paymentIsoCode
    organisation
    name
    slug
    productFamily {
      id
      name
      slug
    }
    description
    shortDescription
    overview
    productCode
    price
    smallPromoImage
    smallPromoImageAltText
  }
`;

export const TEST_PRODUCT_FAMILY_FIELDS = gql`
  fragment TestProductFamilyFields on TestProductFamilyType {
    id
    name
    slug
    metaTitle
    metaDescription
    description
  }
`;

export const SHIPPING_COUNTRIES_QUERY = gql`
  query ShippingCountriesQuery($availableForCheckout: Boolean) {
    shippingCountries(availableForCheckout: $availableForCheckout) {
      id
      name
      isoCode
      postageCost
      stripeSupported
    }
  }
`;

export const CONSUMER_ORDER_TEST_ITEM_TEST_PRODUCT_FIELDS = gql`
  fragment ConsumerOrderTestItemTestProductFields on TestProductType {
    id
    paymentIsoCode
    organisation
    compositeId
    name
    slug
    productType
    listingVisibility
    sampleTypes
    sector
    rrpCurrentPrice
    rrpFullPrice
  }
`;

export const CONSUMER_ORDER_TEST_ITEM_TEST_PRODUCT_OPTION_FIELDS = gql`
  fragment ConsumerOrderTestItemTestProductOptionFields on TestProductOptionType {
    id
    paymentIsoCode
    organisation
    compositeId
    rrpCurrentPrice
    rrpFullPrice
  }
`;

export const ORDER_TEST_ITEM_FIELDS = gql`
  fragment OrderTestItemFields on OrderTestItemType {
    id
    testSentToUser
    sampleReceivedByProvider
    sampleReceivedByLab
    resultsReceivedByProvider
    resultsProcessed
    estimatedResultsAvailableDate
    estimatedResultsAvailableDateHelpText
    defunct
    price
    nameInBasket
    groupBy
    providerTestId
    derivedProgressStatus
    registered
    parent {
      id
    }
    userTestIds
    product {
      ...ConsumerOrderTestItemTestProductFields
    }
    productOption {
      ...ConsumerOrderTestItemTestProductOptionFields
    }
    clinicLocation {
      ...ClinicLocationFields
    }
    clinicBooking {
      id
      url
      status
      details
    }
  }
  ${CLINIC_LOCATION_FIELDS}
  ${CONSUMER_ORDER_TEST_ITEM_TEST_PRODUCT_FIELDS}
  ${CONSUMER_ORDER_TEST_ITEM_TEST_PRODUCT_OPTION_FIELDS}
`;

export const CONSUMER_ORDER_TEST_ITEM_FIELDS = gql`
  fragment ConsumerOrderTestItemFields on OrderTestItemType {
    id
    created
    testSentToUser
    sampleReceivedByProvider
    sampleReceivedByLab
    resultsReceivedByProvider
    resultsProcessed
    estimatedResultsAvailableDate
    estimatedResultsAvailableDateHelpText
    defunct
    price
    nameInBasket
    groupBy
    derivedProgressStatus
    registered
    hasUserReport(mode: REPORT_AVAILABLE)
    userTestSet {
      ...UserTestFieldsTruncated
    }
    product {
      ...ConsumerOrderTestItemTestProductFields
    }
    productOption {
      ...ConsumerOrderTestItemTestProductOptionFields
    }
    clinicLocation {
      ...ClinicLocationFields
    }
    clinicBooking {
      id
      url
      status
      details
    }
    registeredUserFullName
    inboundTracking
    outboundTracking
  }
  ${CLINIC_LOCATION_FIELDS}
  ${CONSUMER_ORDER_TEST_ITEM_TEST_PRODUCT_FIELDS}
  ${CONSUMER_ORDER_TEST_ITEM_TEST_PRODUCT_OPTION_FIELDS}
  ${USER_TEST_FIELDS_TRUNCATED}
`;

export const SOURCE_BASKET_FIELDS = gql`
  fragment SourceBasketFields on OrganisationBasketType {
    id
    payee
    basketTotalPrice
    basketShippingCost
    currencySymbol
    paymentCheckoutSession {
      id
      paymentStatus
    }
  }
`;

export const ORDER_FIELDS = gql`
  fragment OrderFields on OrderType {
    id
    updated
    checkoutDate
    userFullName
    shippingFullName
    shippingFirstName
    shippingLastName
    shippingAddress
    shippingAddress1
    shippingAddress2
    shippingTownCity
    shippingPostalCode
    shippingCountry
    shippingPhone
    billingFirstName
    billingLastName
    billingAddress
    billingPostalCode
    billingCountry
    postageCosts
    discountCode
    fullTotal
    discountedTotal
    discountMeetsRequirements
    status
    email
    derivedPaymentStatus
    derivedProgressStatus
    sourceBasket {
      ...SourceBasketFields
    }
    supplementItems {
      id
      nameInBasket
      groupBy
      price
      product {
        id
        compositeId
        name
        productType
        price
        slug
      }
    }
    testItems {
      ...ConsumerOrderTestItemFields
    }
    refundSet {
      id
      updated
      amount
    }
  }
  ${CONSUMER_ORDER_TEST_ITEM_FIELDS}
  ${SOURCE_BASKET_FIELDS}
`;

export const READ_ONLY_ORDER_TEST_ITEM_FIELDS = gql`
  fragment ReadOnlyOrderTestItemFields on OrderTestItemType {
    id
    price
    nameInBasket
    parent {
      id
    }
    derivedProgressStatus
    registered
    registeredUserFullName
    hasUserReport(mode: REPORT_AVAILABLE)
    estimatedResultsAvailableDate
    estimatedResultsAvailableDateHelpText
    clinicLocation {
      ...ClinicLocationFields
    }
  }
  ${CLINIC_LOCATION_FIELDS}
`;

export const READ_ONLY_ORDER_FIELDS = gql`
  fragment ReadOnlyOrderFields on OrderType {
    id
    checkoutDate
    userFullName
    shippingFullName
    orderingOrganisationName
    shippingAddress1
    shippingAddress2
    shippingTownCity
    shippingPostalCode
    shippingCountry
    totalDiscount
    vat
    refunded
    status
    patientPayCheckoutRedirectUrl
    derivedPaymentStatus
    derivedProgressStatus
    sourceBasket {
      ...SourceBasketFields
    }
    testItems {
      ...ReadOnlyOrderTestItemFields
    }
    supplementItems {
      id
      nameInBasket
      price
    }
  }
  ${READ_ONLY_ORDER_TEST_ITEM_FIELDS}
  ${SOURCE_BASKET_FIELDS}
`;

export const READ_ONLY_ORDER_QUERY = gql`
  query GetReadOnlyOrder($id: ID!) {
    order(id: $id) {
      ...ReadOnlyOrderFields
    }
  }
  ${READ_ONLY_ORDER_FIELDS}
`;

export const CONSUMER_ORDER_FIELDS = gql`
  fragment ConsumerOrderFields on OrderType {
    id
    updated
    practitionerWhoPlacedOrderFullName
    orderingOrganisationName
    orderCopiedFrom {
      id
    }
    checkoutDate
    userFullName
    shippingFullName
    shippingFirstName
    shippingLastName
    shippingAddress
    shippingAddress1
    shippingAddress2
    shippingTownCity
    shippingPostalCode
    shippingCountry
    shippingPhone
    billingFirstName
    billingLastName
    billingAddress
    billingPostalCode
    billingCountry
    postageCosts
    discountCode
    fullTotal
    discountedTotal
    totalDiscount
    vat
    refunded
    discountMeetsRequirements
    status
    email
    patientPayCheckoutRedirectUrl
    supplementItems {
      id
      nameInBasket
      groupBy
      price
      product {
        id
        compositeId
        name
        productType
        price
        slug
      }
      created
    }
    testItems {
      ...ConsumerOrderTestItemFields
    }
    refundSet {
      id
      updated
      amount
    }
    derivedPaymentStatus
    derivedProgressStatus
    sourceBasket {
      ...SourceBasketFields
    }
  }
  ${CONSUMER_ORDER_TEST_ITEM_FIELDS}
  ${SOURCE_BASKET_FIELDS}
`;

export const ORDER_LIST_FIELDS = gql`
  fragment OrderListFields on OrderType {
    id
    checkoutDate
    estimatedResultsAvailableDate
    estimatedResultsAvailableDateHelpText
    derivedPaymentStatus
    derivedProgressStatus
    sourceBasket {
      ...SourceBasketFields
    }
    userFullName
    shippingFullName
    discountedTotal
    postageCosts
    status
    patientPayCheckoutRedirectUrl
  }
  ${SOURCE_BASKET_FIELDS}
`;

export const ORDER_QUERY = gql`
  query GetOrder($id: ID!) {
    order(id: $id) {
      ...ConsumerOrderFields
      sourceBasket {
        ...SourceBasketFields
      }
    }
  }
  ${CONSUMER_ORDER_FIELDS}
  ${SOURCE_BASKET_FIELDS}
`;

export const ORDERS_QUERY = gql`
  query OrderQuery($userId: ID) {
    orders(userId: $userId) {
      ...ConsumerOrderFields
    }
  }
  ${CONSUMER_ORDER_FIELDS}
`;

export const ORDER_TEST_ITEMS_QUERY = gql`
  query GetOrderTestItems($userId: ID) {
    orderTestItems(userId: $userId) {
      ...OrderTestItemFields
    }
  }
  ${ORDER_TEST_ITEM_FIELDS}
`;

// Mutations

export const ADD_ITEM_TO_BASKET = gql`
  mutation AddItemToBasket($compositeId: ID!, $clinicLocationId: ID) {
    addItemToBasket(clinicLocationId: $clinicLocationId, compositeId: $compositeId) {
      status
    }
  }
`;

export const REMOVE_ITEM_FROM_BASKET = gql`
  mutation RemoveItemFromBasket($compositeId: ID!, $clinicLocationId: ID) {
    removeItemFromBasket(clinicLocationId: $clinicLocationId, compositeId: $compositeId) {
      status
    }
  }
`;

export const STORE_INCOMING_DISCOUNT_CODE = gql`
  mutation StoreIncomingDiscountCode($discountCode: String!) {
    storeIncomingDiscountCode(discountCode: $discountCode) {
      status
    }
  }
`;

export const REMOVE_PAYMENT_METHOD = gql`
  mutation RemovePaymentMethod {
    removePaymentMethod {
      status
    }
  }
`;

export const CHECKOUT_MUTATION = gql`
  mutation CheckoutMutation($input: CheckoutMutationInput!) {
    checkoutMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const UPDATE_DISCOUNT_MUTATION = gql`
  mutation UpdateDiscountMutation($input: UpdateDiscountMutationInput!) {
    updateDiscountMutation(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const SUPPLEMENT_QUERY = gql`
  query SupplementQuery($slug: String!) {
    supplement(slug: $slug) {
      id
      compositeId
      name
      productCode
      price
      description
      directions
      allergens
      slug
      nutritionalInfoImage
      productImage
      status
      supplementCategories {
        id
        name
      }
    }
  }
`;

export const SUPPLEMENTS_QUERY = gql`
  query SupplementsQuery {
    supplements {
      id
      name
      slug
      compositeId
    }
  }
`;

export const RESCHEDULE_BOOKING_MUTATION = gql`
  mutation RescheduleBookingMutation($orderTestItemId: ID!) {
    rescheduleBookingMutation(orderTestItemId: $orderTestItemId) {
      status
      errors {
        field
        messages
      }
    }
  }
`;

export const LATEST_SAMPLE_FOR_PATIENT_QUERY = gql`
  query LatestSampleForPatient($userId: ID!, $organisationId: ID!, $productId: ID!) {
    latestSampleForPatient(
      userId: $userId
      organisationId: $organisationId
      productId: $productId
    ) {
      id
      providerItemId
      providerTestId
      sampleReceivedByLab
      isValidForCyrexSample
      order {
        id
      }
    }
  }
`;

export const CANCEL_BOOKING_MUTATION = gql`
  mutation CancelBookingMutation($orderTestItemId: ID!) {
    cancelBookingMutation(orderTestItemId: $orderTestItemId) {
      status
      errors {
        field
        messages
      }
    }
  }
`;

const CURRENCY_FIELDS = gql`
  fragment CurrencyFields on CurrencyType {
    id
    isoCode
  }
`;

export const SAMPLE_REPORT_TYPE_FIELDS = gql`
  fragment SampleReportTypeFields on SampleReportType {
    id
    name
    pdf
    filename
  }
`;

const CONSUMER_TEST_PRODUCT_DETAIL_FIELDS = gql`
  fragment ConsumerTestProductDetailFields on TestProductType {
    id
    paymentIsoCode
    organisation
    compositeId
    productAudience
    name
    slug
    status
    sampleTypes
    rrpCurrentPrice
    rrpFullPrice
    reportPdf
    sampleLabReports {
      ...SampleReportTypeFields
    }
    sampleGeneratedReports {
      ...SampleReportTypeFields
    }
    instructions {
      ...SampleReportTypeFields
    }
    supportingDocuments {
      ...SampleReportTypeFields
    }
    content {
      ...TestProductContentFields
      description
      shortDescription
      collectingYourSampleVideo
      collectingYourSampleImage
      carouselImage1: image1
      carouselImage2: image2
      carouselImage3: image3
    }
    testDataFiles {
      ...TestDataFileFields
    }
    options {
      ...ConsumerTestProductOptionFields
    }
  }
  ${TEST_DATA_FILE_FIELDS}
  ${TEST_PRODUCT_CONTENT_FIELDS}
  ${CONSUMER_TEST_PRODUCT_OPTION_FIELDS}
  ${SAMPLE_REPORT_TYPE_FIELDS}
`;

export const CONSUMER_TEST_PRODUCT_DETAIL_PAGE_QUERY = gql`
  query ConsumerTestProductDetailPageQuery($slug: String!) {
    consumerTestProduct(slug: $slug) {
      ...ConsumerTestProductDetailFields
    }
  }
  ${CONSUMER_TEST_PRODUCT_DETAIL_FIELDS}
`;

export const CONSUMER_TEST_PRODUCT_DETAIL_QUERY = gql`
  query ConsumerTestProductQuery($id: ID) {
    consumerTestProduct(id: $id) {
      ...ConsumerTestProductDetailFields
    }
  }
  ${CONSUMER_TEST_PRODUCT_DETAIL_FIELDS}
`;

export const PRACTITIONER_TEST_PRODUCT_DETAIL_QUERY = gql`
  query PractitionerTestProductQuery(
    $id: ID
    $slug: String
    $paymentIsoCode: String
    $organisation: ID
    $userId: ID
  ) {
    practitionerTestProduct(
      id: $id
      slug: $slug
      paymentIsoCode: $paymentIsoCode
      organisation: $organisation
      userId: $userId
    ) {
      id
      productCode
      listingVisibility
      isCyrex
      supplier {
        id
        name
      }
      independentlyPurchasableAddon
      paymentIsoCode
      functionalDatapointCount
      organisation
      productAudience
      isStudentAllowed
      reportPdf
      sampleGeneratedReports {
        ...SampleReportTypeFields
      }
      sampleLabReports {
        ...SampleReportTypeFields
      }
      instructions {
        ...SampleReportTypeFields
      }
      supportingDocuments {
        ...SampleReportTypeFields
      }
      name
      productCode
      isGenesTest
      slug
      sampleTypes
      tradeCurrentPrice
      tradeFullPrice
      rrpFullPrice
      exvatFullPrice
      status
      addons {
        id
        status
        listingVisibility
        independentlyPurchasableAddon
        paymentIsoCode
        organisation
        name
        functionalDatapointCount
        dnaDatapointCount
        overlappingProductIds
        content {
          id
          description
          iconType
          reportIcon
          reportAddonsDescription
          shortDescription
          biomarkersTested
          informationPanels {
            ...TestProductInformationPanelFields
          }
        }
        tradeCurrentPrice
        exvatFullPrice
      }
      requiredAddons {
        id
        status
        listingVisibility
        independentlyPurchasableAddon
        paymentIsoCode
        organisation
        name
        functionalDatapointCount
        dnaDatapointCount
        overlappingProductIds
        content {
          id
          description
          iconType
          reportIcon
          reportAddonsDescription
          shortDescription
          biomarkersTested
          informationPanels {
            ...TestProductInformationPanelFields
          }
        }
        tradeCurrentPrice
        exvatFullPrice
      }
      content {
        ...TestProductContentFields
        reportAddonsDescription
        description
        shortDescription
        collectingYourSampleVideo
        collectingYourSampleImage
        carouselImage1: image1
        carouselImage2: image2
        carouselImage3: image3
      }
      testDataFiles {
        ...TestDataFileFields
      }
      options {
        ...PractitionerTestProductOptionFields
      }
    }
  }
  ${TEST_DATA_FILE_FIELDS}
  ${PRACTITIONER_TEST_PRODUCT_OPTION_FIELDS}
  ${TEST_PRODUCT_CONTENT_FIELDS}
  ${SAMPLE_REPORT_TYPE_FIELDS}
`;

const CONSUMER_TEST_PRODUCT_CONNECTION_FIELDS = gql`
  fragment ConsumerTestProductConnectionFields on TestProductType {
    id
    paymentIsoCode
    organisation
    slug
    productAudience
    name
    sampleTypes
    rrpCurrentPrice
    rrpFullPrice
    addons {
      id
      paymentIsoCode
      organisation
    }
    laboratory {
      id
      name
      productImageUrl
    }
    content {
      ...TestProductContentFields
      image1
      image2
      image3
    }
    testDataFiles {
      ...TestDataFileFields
    }
  }
  ${TEST_DATA_FILE_FIELDS}
  ${TEST_PRODUCT_CONTENT_FIELDS}
`;

export const PRACTITIONER_TEST_PRODUCT_CONNECTION_FIELDS = gql`
  fragment PractitionerTestProductConnectionFields on TestProductType {
    id
    functionalDatapointCount
    dnaDatapointCount
    questionsCount
    paymentIsoCode
    organisation
    slug
    productAudience
    isStudentAllowed
    isCyrex
    isPractitionerFavourite
    name
    sampleTypes
    status
    laboratory {
      id
      name
      productImageUrl
    }
    tradeCurrentPrice
    tradeFullPrice
    rrpFullPrice
    exvatFullPrice
    content {
      ...TestProductContentFields
      reportIcon
    }
    testDataFiles {
      ...TestDataFileFields
    }
    options {
      id
      paymentIsoCode
      organisation
    }
    addons {
      id
      paymentIsoCode
      organisation
    }
  }
  ${TEST_DATA_FILE_FIELDS}
  ${TEST_PRODUCT_CONTENT_FIELDS}
`;

export const CONSUMER_TEST_PRODUCT_CONNECTIONS_QUERY = gql`
  query ConsumerTestProductConnectionsQuery(
    $first: Int!
    $after: String
    $orderBy: String
    $search: String
    $productAudience: String
    $gender: String
    $testingServices: String
    $healthAreas: String
    $sampleTypes: String
    $isoCode: String
    $paymentIsoCode: String
  ) {
    consumerTestProductConnections(
      first: $first
      after: $after
      orderBy: $orderBy
      search: $search
      productAudience: $productAudience
      gender: $gender
      testingServices: $testingServices
      healthAreas: $healthAreas
      sampleTypes: $sampleTypes
      isoCode: $isoCode
      paymentIsoCode: $paymentIsoCode
    ) {
      edges {
        node {
          ...ConsumerTestProductConnectionFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${CONSUMER_TEST_PRODUCT_CONNECTION_FIELDS}
`;

export const CONSUMER_TEST_PRODUCT_WITH_OPTIONS_CONNECTIONS_QUERY = gql`
  query ConsumerTestProductConnectionsQuery(
    $first: Int!
    $after: String
    $orderBy: String
    $search: String
    $productAudience: String
    $gender: String
    $testingServices: String
    $healthAreas: String
    $sampleTypes: String
    $isoCode: String
    $paymentIsoCode: String
  ) {
    consumerTestProductConnections(
      first: $first
      after: $after
      orderBy: $orderBy
      search: $search
      productAudience: $productAudience
      gender: $gender
      testingServices: $testingServices
      healthAreas: $healthAreas
      sampleTypes: $sampleTypes
      isoCode: $isoCode
      paymentIsoCode: $paymentIsoCode
    ) {
      edges {
        node {
          ...ConsumerTestProductWithOptionsConnectionFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${CONSUMER_TEST_PRODUCT_WITH_OPTIONS_CONNECTION_FIELDS}
`;

export const PRACTITIONER_TEST_PRODUCT_CONNECTIONS_QUERY = gql`
  query PractitionerTestProductConnectionsQuery(
    $first: Int!
    $after: String
    $orderBy: String
    $search: String
    $productAudience: String
    $gender: String
    $testingServices: String
    $healthAreas: String
    $sampleTypes: String
    $isoCode: String
    $paymentIsoCode: String
    $organisation: ID
    $filterByFavourites: Boolean
    $featured: Boolean
    $listingVisibility: String
    $userId: ID
  ) {
    practitionerTestProductConnections(
      first: $first
      after: $after
      orderBy: $orderBy
      search: $search
      productAudience: $productAudience
      gender: $gender
      testingServices: $testingServices
      healthAreas: $healthAreas
      sampleTypes: $sampleTypes
      isoCode: $isoCode
      paymentIsoCode: $paymentIsoCode
      organisation: $organisation
      filterByFavourites: $filterByFavourites
      featured: $featured
      listingVisibility: $listingVisibility
      userId: $userId
    ) {
      edges {
        node {
          ...PractitionerTestProductConnectionFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${PRACTITIONER_TEST_PRODUCT_CONNECTION_FIELDS}
`;

export const SUPPLEMENT_CONNECTION_FIELDS = gql`
  fragment SupplementConnectionFields on SupplementType {
    id
    compositeId
    name
    slug
    brandName
    size
    price
    dietryRestrictions: excludedDietPreferences {
      id
      name
    }
    doseType
    productImage
    status
    productCode
    description
    supplementIngredients
  }
`;

export const SUPPLEMENT_DETAIL_QUERY = gql`
  query SupplementDetailQuery($slug: String!, $userId: ID) {
    supplement(slug: $slug) {
      ...SupplementConnectionFields
      description
      directions
      relatedUserResults(userId: $userId) {
        id
        ...UserResultFieldsTruncated
        chartConfiguration {
          ...ChartConfigurationFields
        }
        resultState {
          id
          name
        }
      }
    }
  }
  ${CHART_CONFIGURATION_FIELDS}
  ${SUPPLEMENT_CONNECTION_FIELDS}
  ${USER_RESULT_FIELDS_TRUNCATED}
`;

export const SUPPLEMENT_CONNECTIONS_QUERY = gql`
  query SupplementConnectionsQuery(
    $first: Int!
    $after: String
    $orderBy: String
    $search: String
    $brandNames: String
    $doseTypes: String
    $dietryRestrictions: String
  ) {
    supplementConnections(
      first: $first
      after: $after
      orderBy: $orderBy
      search: $search
      brandNames: $brandNames
      doseTypes: $doseTypes
      dietryRestrictions: $dietryRestrictions
    ) {
      edges {
        node {
          ...SupplementConnectionFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${SUPPLEMENT_CONNECTION_FIELDS}
`;

/**
 * Supplement listing page needs dietryRestrictions,
 * types and brands.  TestProductListingPage needs
 * sampleTypes, testingServices,
 * and categories.
 *
 * Get them all in one query so when user switches between
 * supplements and tests we don't get a loading icon.
 * Smoother experience this way.
 */

export const SHOP_LISTING_FILTERS_QUERY = gql`
  query FiltersQuery {
    dietryRestrictions: dietPreferences {
      id
      name
    }
    types: supplementDoseTypes
    brands: supplementBrands
    sampleTypes
    testingServices
    categories: healthAreas {
      ...HealthAreaFields
    }
  }
  ${HEALTH_AREA_FIELDS}
`;

export const OMNOS_TEST_PRODUCTS_QUERY = gql`
  query OmnosTestProductsQuery(
    $productAudience: String
    $orderBy: String
    $limit: Int
    $isMostPopularTest: Boolean
  ) {
    consumerTestProducts(
      productAudience: $productAudience
      orderBy: $orderBy
      limit: $limit
      isMostPopularTest: $isMostPopularTest
    ) {
      id
      paymentIsoCode
      isPractitionerFavourite
      organisation
      name
      slug
      laboratory {
        id
        name
        productImageUrl
      }
      sampleTypes
      productAudience
      productFamily {
        id
        slug
      }
      rrpCurrentPrice
      rrpFullPrice
      content {
        id
        image1
        categories: healthAreas {
          id
          name
        }
        testType
        shortDescription
      }
    }
  }
`;
